/**
 * Customer 360 — Stage 1 deliverable.
 *
 * Composes two backend endpoints:
 *   - `/api/users/{id}/`           — verification, contact, last_login,
 *                                    last_order_date (UserDetailSerializer)
 *   - `/staff/api/user/{id}/`      — last-known geo + lifetime summary
 *                                    (staff UserSerializer)
 *   - `/api/users/{id}/cart/items/`  live cart snapshot
 *   - `/staff/api/orders/?customer_id=…` all customer orders (paginated)
 *
 * Last-known location uses OSM + Leaflet (`LastKnownLocationMap`).
 */
import { useMemo, useState } from 'react';
import { ArrowLeft, ChevronRight, Mail, MapPin, Package, Phone, ShoppingCart, User as UserIcon } from 'lucide-react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import { useCustomerCart, useCustomerDetail, useCustomerStaffSummary } from '@/api/legacy/customers';
import { useOrdersList, type OrderRow } from '@/api/legacy/orders';
import { fmt } from '@/lib/format';
import { statusLabel, statusTone } from '@/lib/status';
import { LastKnownLocationMap } from '@/components/maps/LastKnownLocationMap';
import { DataView } from '@/components/data/DataView';

const CUSTOMER_ORDERS_PAGE_SIZE = 10;

export function CustomerDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = params.id ? Number(params.id) : null;
  const [ordersPage, setOrdersPage] = useState(1);
  const detail = useCustomerDetail(userId);
  const staffSummary = useCustomerStaffSummary(userId);
  const cart = useCustomerCart(userId);
  const customerOrders = useOrdersList({
    customerId: userId,
    page: ordersPage,
    pageSize: CUSTOMER_ORDERS_PAGE_SIZE,
    sortBy: 'created_timestamp',
    sortOrder: 'desc',
  });
  const orderColumns = useMemo<ColumnDef<OrderRow, unknown>[]>(
    () => [
      {
        id: 'order',
        header: 'Order',
        cell: ({ row }) => <span className="font-mono text-xs">#{row.original.id}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge tone={statusTone(row.original.status)}>
            {statusLabel(row.original.status)}
          </Badge>
        ),
      },
      {
        id: 'total',
        header: () => <div className="text-right">Total</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right font-medium">
            {fmt.npr(row.original.final_total)}
          </div>
        ),
      },
      {
        id: 'created',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {row.original.created_timestamp
              ? format(new Date(row.original.created_timestamp), 'MMM d, HH:mm')
              : '—'}
          </span>
        ),
      },
    ],
    [],
  );

  if (userId == null) {
    return <ErrorState title="Missing customer id" />;
  }

  // KPI roll-up: prefer the staff serializer's summary (completed orders +
  // total revenue) and fall back to the orders-list `total_items` for total
  // count when the staff endpoint is unreachable.
  const summary = staffSummary.data?.summary ?? null;
  const totalOrders = summary?.total_orders ?? customerOrders.data?.total_items ?? null;
  const totalSpent = summary?.total_sum ?? null;
  const avgOrderValue =
    totalSpent != null && totalOrders && totalOrders > 0 ? Number(totalSpent) / totalOrders : null;
  const lastOrderAt = detail.data?.last_order_date ?? null;
  const phone = detail.data?.phone_number ?? staffSummary.data?.phone_number ?? null;
  const lat = staffSummary.data?.last_latitude;
  const lng = staffSummary.data?.last_longitude;
  const lastAddress = staffSummary.data?.last_address ?? null;
  const customerName = detail.data?.name || staffSummary.data?.name || `User #${userId}`;
  const fromState =
    typeof (location.state as { from?: unknown } | null)?.from === 'string'
      ? String((location.state as { from?: string }).from)
      : null;
  const handleBack = () => {
    if (fromState) navigate(fromState);
    else navigate(-1);
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={handleBack} className="gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </Button>
        <Link to="/customers" className="text-xs text-muted-foreground hover:text-foreground">
          Customers <ChevronRight className="inline h-3 w-3" /> #{userId}
        </Link>
      </div>

      <PageHeader
        breadcrumbs
        eyebrow="Customer"
        title={customerName}
        description={
          detail.data || staffSummary.data ? (
            <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              {detail.data?.is_verified ? <Badge tone="success">verified</Badge> : <Badge tone="warning">unverified</Badge>}
              {detail.data?.username ? <span>@{detail.data.username}</span> : null}
              {(detail.data?.date_joined || staffSummary.data?.date_joined) ? (
                <span>
                  · joined{' '}
                  {format(new Date(String(detail.data?.date_joined ?? staffSummary.data?.date_joined)), 'MMM yyyy')}
                </span>
              ) : null}
            </span>
          ) : (
            'Loading…'
          )
        }
      />

      {detail.isError ? <ErrorState onRetry={() => void detail.refetch()} /> : null}

      <div className="mt-4 grid gap-4 lg:grid-cols-4">
        <Stat label="Lifetime orders" value={totalOrders} fmtFn={(n) => fmt.number(n)} />
        <Stat label="Lifetime spend" value={totalSpent} fmtFn={(n) => fmt.npr(n)} />
        <Stat label="Avg order value" value={avgOrderValue} fmtFn={(n) => fmt.npr(n)} />
        <Stat
          label="Last order"
          value={lastOrderAt}
          fmtFn={(v) => (v ? format(new Date(String(v)), 'MMM d, yyyy') : '—')}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2">
              <UserIcon className="h-4 w-4" /> Contact
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            {detail.isLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : (
              <>
                {phone ? (
                  <KV icon={<Phone className="h-3 w-3" />} label="Phone" value={phone} />
                ) : null}
                {detail.data?.email ? (
                  <KV icon={<Mail className="h-3 w-3" />} label="Email" value={detail.data.email} />
                ) : null}
                {detail.data?.last_login ? (
                  <KV
                    label="Last login"
                    value={format(new Date(detail.data.last_login), 'MMM d, HH:mm')}
                  />
                ) : null}
                {detail.data?.last_active ? (
                  <KV
                    label="Last active"
                    value={format(new Date(detail.data.last_active), 'MMM d, HH:mm')}
                  />
                ) : null}
                {!phone && !detail.data?.email && !detail.data?.last_login ? (
                  <span className="text-muted-foreground">No contact info on file.</span>
                ) : null}
              </>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" /> Cart now
            </CardTitle>
            {(cart.data?.items?.length ?? cart.data?.results?.length ?? 0) > 0 ? (
              <Badge tone="info">{cart.data?.items?.length ?? cart.data?.results?.length}</Badge>
            ) : null}
          </CardHeader>
          <CardBody className="text-sm">
            {cart.isLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : cart.isError ? (
              // "No active cart" is a sales signal someone may act on, so it
              // must not double as the failure message.
              <button
                type="button"
                onClick={() => void cart.refetch()}
                className="press rounded text-xs text-danger underline decoration-dotted focus-ring"
              >
                Couldn’t load the cart — retry
              </button>
            ) : (() => {
              const items = cart.data?.items ?? cart.data?.results ?? [];
              if (!items.length) return <span className="text-muted-foreground">No active cart.</span>;
              return (
                <ul className="space-y-1">
                  {items.slice(0, 6).map((it) => (
                    <li key={it.id} className="flex items-center justify-between gap-2">
                      <span className="min-w-0 flex-1 truncate">
                        {it.product_name ?? it.name ?? `#${it.id}`}
                      </span>
                      <span className="kpi-num text-xs text-muted-foreground">
                        ×{fmt.number(it.quantity)}
                      </span>
                    </li>
                  ))}
                  {items.length > 6 ? (
                    <li className="text-[11px] text-muted-foreground">+{items.length - 6} more</li>
                  ) : null}
                </ul>
              );
            })()}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2">
              <MapPin className="h-4 w-4" /> Last known
            </CardTitle>
          </CardHeader>
          <CardBody className="text-sm">
            {lat != null && lng != null ? (
              <CoordsPreview lat={Number(lat)} lng={Number(lng)} address={lastAddress} />
            ) : (
              <span className="text-muted-foreground">No location on file.</span>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="mt-4">
        <DataView<OrderRow>
          title="Orders"
          description="All orders placed by this customer, newest first."
          data={customerOrders.data?.results ?? []}
          columns={orderColumns}
          isLoading={customerOrders.isLoading}
          isError={customerOrders.isError}
          onRetry={() => void customerOrders.refetch()}
          pagination={{
            page: ordersPage,
            pageSize: CUSTOMER_ORDERS_PAGE_SIZE,
            total: customerOrders.data?.total_items ?? 0,
            onPageChange: setOrdersPage,
          }}
          onRowClick={(row) => navigate(`/orders/${row.id}`, { state: { from: `/customers/${userId}` } })}
          emptyTitle="No orders yet"
          emptyDescription="This customer hasn't placed an order yet."
          toolbar={
            <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              <Package className="h-4 w-4" aria-hidden />
              {customerOrders.data?.total_items != null
                ? `${fmt.number(customerOrders.data.total_items)} total`
                : 'Customer order history'}
            </div>
          }
        />
      </div>
    </>
  );
}

function Stat({ label, value, fmtFn }: { label: string; value: number | string | null; fmtFn: (v: number | string) => string }) {
  return (
    <Card>
      <CardBody>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-display kpi-num mt-1 text-2xl font-bold">
          {value == null ? '—' : fmtFn(value as never)}
        </div>
      </CardBody>
    </Card>
  );
}

function KV({ icon, label, value }: { icon?: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        {icon}
        {label}
      </span>
      <span className="text-right">{value}</span>
    </div>
  );
}

function CoordsPreview({ lat, lng, address }: { lat: number; lng: number; address?: string | null }) {
  return (
    <div>
      {address ? (
        <div className="mb-1 line-clamp-2 text-sm" title={address}>
          {address}
        </div>
      ) : null}
      <div className="font-mono text-xs text-muted-foreground">
        {lat.toFixed(5)}, {lng.toFixed(5)}
      </div>
      <LastKnownLocationMap
        latitude={lat}
        longitude={lng}
        address={address}
        className="mt-2 overflow-hidden rounded-md border border-border"
        height={176}
      />
      <a
        href={`https://www.google.com/maps?q=${lat},${lng}`}
        target="_blank"
        rel="noreferrer"
        className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
      >
        Open in Maps <ChevronRight className="h-3 w-3" />
      </a>
    </div>
  );
}
